import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ─────────────────────────────────────────────────────────────────────────────
// Shared, deduplicated queries. Every consumer of `useMakeModel` shares the
// same three fetches — mm_dict + get_make_counts + get_model_counts — through
// React Query keys with a long staleTime (30 min). Concurrent mounts collapse
// into a single network request instead of 3–4 parallel ones.
// ─────────────────────────────────────────────────────────────────────────────

const MM_STALE_MS = 30 * 60 * 1000;

const fetchMmDict = async () => {
  const { data, error } = await supabase
    .from('mm_dict')
    .select('make, model, active, category_id, categories:category_id(code)')
    .eq('active', true)
    .order('make', { ascending: true });
  if (error) throw error;
  return data || [];
};

const fetchMakeCounts = async () => {
  const { data, error } = await supabase.rpc('get_make_counts', {
    p_country_code: undefined,
    p_category_code: undefined,
  });
  if (error) throw error;
  return (data || []) as Array<{ make: string; count: number }>;
};

const fetchModelCounts = async () => {
  const { data, error } = await supabase.rpc('get_model_counts', {
    p_country_code: undefined,
    p_category_code: undefined,
  });
  if (error) throw error;
  return (data || []) as Array<{ make: string; model: string; count: number }>;
};



interface MakeModelData {
  make: string;
  model: string;
  count: number;
}

interface GroupedData {
  makes: Array<{
    value: string;
    label: string;
    count: number;
  }>;
  modelsByMake: Record<string, Array<{
    value: string;
    label: string;
    count: number;
  }>>;
}

const FALLBACKS: Record<string, GroupedData> = {
  truck: {
    makes: [
      { value: 'Scania', label: 'Scania', count: 0 },
      { value: 'Volvo Trucks', label: 'Volvo Trucks', count: 0 },
      { value: 'MAN', label: 'MAN', count: 0 },
      { value: 'DAF', label: 'DAF', count: 0 },
      { value: 'Mercedes-Benz Trucks', label: 'Mercedes-Benz Trucks', count: 0 }
    ],
    modelsByMake: {
      Scania: [
        { value: 'R Series', label: 'R Series', count: 0 },
        { value: 'S Series', label: 'S Series', count: 0 }
      ],
      'Volvo Trucks': [
        { value: 'FH', label: 'FH', count: 0 },
        { value: 'FM', label: 'FM', count: 0 }
      ],
      MAN: [
        { value: 'TGX', label: 'TGX', count: 0 },
        { value: 'TGS', label: 'TGS', count: 0 }
      ],
      DAF: [
        { value: 'XF', label: 'XF', count: 0 },
        { value: 'CF', label: 'CF', count: 0 }
      ],
      'Mercedes-Benz Trucks': [
        { value: 'Actros', label: 'Actros', count: 0 },
        { value: 'Arocs', label: 'Arocs', count: 0 }
      ]
    }
  },
  motorcycle: {
    makes: [
      { value: 'Yamaha', label: 'Yamaha', count: 0 },
      { value: 'Honda', label: 'Honda', count: 0 },
      { value: 'BMW Motorrad', label: 'BMW Motorrad', count: 0 },
      { value: 'Kawasaki', label: 'Kawasaki', count: 0 }
    ],
    modelsByMake: {
      Yamaha: [
        { value: 'MT-07', label: 'MT-07', count: 0 },
        { value: 'YZF-R3', label: 'YZF-R3', count: 0 }
      ],
      Honda: [
        { value: 'CB500F', label: 'CB500F', count: 0 },
        { value: 'Africa Twin', label: 'Africa Twin', count: 0 }
      ],
      'BMW Motorrad': [
        { value: 'R 1250 GS', label: 'R 1250 GS', count: 0 },
        { value: 'F 900 R', label: 'F 900 R', count: 0 }
      ],
      Kawasaki: [
        { value: 'Z650', label: 'Z650', count: 0 },
        { value: 'Ninja 400', label: 'Ninja 400', count: 0 }
      ]
    }
  },
  van: {
    makes: [
      { value: 'Ford', label: 'Ford', count: 0 },
      { value: 'Mercedes-Benz', label: 'Mercedes-Benz', count: 0 },
      { value: 'Volkswagen', label: 'Volkswagen', count: 0 }
    ],
    modelsByMake: {
      Ford: [
        { value: 'Transit', label: 'Transit', count: 0 },
        { value: 'Transit Custom', label: 'Transit Custom', count: 0 }
      ],
      'Mercedes-Benz': [
        { value: 'Sprinter', label: 'Sprinter', count: 0 },
        { value: 'Vito', label: 'Vito', count: 0 }
      ],
      Volkswagen: [
        { value: 'Transporter', label: 'Transporter', count: 0 },
        { value: 'Crafter', label: 'Crafter', count: 0 }
      ]
    }
  },
  boat: {
    makes: [
      { value: 'Bayliner', label: 'Bayliner', count: 0 },
      { value: 'Quicksilver', label: 'Quicksilver', count: 0 }
    ],
    modelsByMake: {
      Bayliner: [
        { value: 'Element E16', label: 'Element E16', count: 0 },
        { value: 'VR5', label: 'VR5', count: 0 }
      ],
      Quicksilver: [
        { value: 'Activ 555', label: 'Activ 555', count: 0 },
        { value: 'Activ 675', label: 'Activ 675', count: 0 }
      ]
    }
  },
  agricultural: {
    makes: [
      { value: 'John Deere', label: 'John Deere', count: 0 },
      { value: 'New Holland', label: 'New Holland', count: 0 }
    ],
    modelsByMake: {
      'John Deere': [
        { value: '6R', label: '6R', count: 0 },
        { value: '5E', label: '5E', count: 0 }
      ],
      'New Holland': [
        { value: 'T7', label: 'T7', count: 0 },
        { value: 'T5', label: 'T5', count: 0 }
      ]
    }
  },
  construction: {
    makes: [
      { value: 'Caterpillar', label: 'Caterpillar', count: 0 },
      { value: 'JCB', label: 'JCB', count: 0 }
    ],
    modelsByMake: {
      Caterpillar: [
        { value: '320', label: '320', count: 0 },
        { value: '306', label: '306', count: 0 }
      ],
      JCB: [
        { value: '3CX', label: '3CX', count: 0 },
        { value: '4CX', label: '4CX', count: 0 }
      ]
    }
  },
  trailer: {
    makes: [
      { value: 'Schmitz Cargobull', label: 'Schmitz Cargobull', count: 0 },
      { value: 'Krone', label: 'Krone', count: 0 }
    ],
    modelsByMake: {
      'Schmitz Cargobull': [
        { value: 'S.KO', label: 'S.KO', count: 0 },
        { value: 'S.CS', label: 'S.CS', count: 0 }
      ],
      Krone: [
        { value: 'Cool Liner', label: 'Cool Liner', count: 0 },
        { value: 'Dry Liner', label: 'Dry Liner', count: 0 }
      ]
    }
  },
  atv: {
    makes: [
      { value: 'Can-Am', label: 'Can-Am', count: 0 },
      { value: 'Polaris', label: 'Polaris', count: 0 }
    ],
    modelsByMake: {
      'Can-Am': [
        { value: 'Outlander', label: 'Outlander', count: 0 },
        { value: 'Renegade', label: 'Renegade', count: 0 }
      ],
      Polaris: [
        { value: 'Sportsman', label: 'Sportsman', count: 0 },
        { value: 'RZR', label: 'RZR', count: 0 }
      ]
    }
  },
  tractor: {
    makes: [
      { value: 'John Deere', label: 'John Deere', count: 0 },
      { value: 'New Holland', label: 'New Holland', count: 0 },
      { value: 'Caterpillar', label: 'Caterpillar', count: 0 },
      { value: 'Case IH', label: 'Case IH', count: 0 },
      { value: 'Massey Ferguson', label: 'Massey Ferguson', count: 0 },
      { value: 'Kubota', label: 'Kubota', count: 0 },
      { value: 'Fendt', label: 'Fendt', count: 0 },
      { value: 'JCB', label: 'JCB', count: 0 }
    ],
    modelsByMake: {
      'John Deere': [
        { value: '6R', label: '6R', count: 0 },
        { value: '5E', label: '5E', count: 0 },
        { value: '7R', label: '7R', count: 0 }
      ],
      'New Holland': [
        { value: 'T7', label: 'T7', count: 0 },
        { value: 'T5', label: 'T5', count: 0 },
        { value: 'T6', label: 'T6', count: 0 }
      ],
      Caterpillar: [
        { value: 'D6', label: 'D6', count: 0 },
        { value: '320', label: '320', count: 0 },
        { value: '306', label: '306', count: 0 }
      ],
      'Case IH': [
        { value: 'Puma', label: 'Puma', count: 0 },
        { value: 'Maxxum', label: 'Maxxum', count: 0 }
      ],
      'Massey Ferguson': [
        { value: '5700', label: '5700', count: 0 },
        { value: '6700', label: '6700', count: 0 }
      ],
      Kubota: [
        { value: 'M7', label: 'M7', count: 0 },
        { value: 'M5', label: 'M5', count: 0 }
      ],
      Fendt: [
        { value: '700 Vario', label: '700 Vario', count: 0 },
        { value: '900 Vario', label: '900 Vario', count: 0 }
      ],
      JCB: [
        { value: 'Fastrac', label: 'Fastrac', count: 0 },
        { value: '3CX', label: '3CX', count: 0 }
      ]
    }
  }
};

export const useMakeModel = (vehicleType?: string, _countryCode?: string) => {
  const mmDictQ = useQuery({ queryKey: ['mm-dict-all'], queryFn: fetchMmDict, staleTime: MM_STALE_MS });
  const makeCountsQ = useQuery({ queryKey: ['mm-make-counts'], queryFn: fetchMakeCounts, staleTime: MM_STALE_MS });
  const modelCountsQ = useQuery({ queryKey: ['mm-model-counts'], queryFn: fetchModelCounts, staleTime: MM_STALE_MS });

  const loading = mmDictQ.isLoading || makeCountsQ.isLoading || modelCountsQ.isLoading;
  const error = (mmDictQ.error || makeCountsQ.error || modelCountsQ.error) as Error | null;

  const data = useMemo<GroupedData>(() => {
    const mmData = mmDictQ.data;
    const realMakeCounts = makeCountsQ.data;
    const realModelCounts = modelCountsQ.data;
    if (!mmData || !realMakeCounts || !realModelCounts) {
      return { makes: [], modelsByMake: {} };
    }

    const normalizeMakeName = (make: string): string => {
      const lower = (make || '').toLowerCase().trim();
      if (lower.includes('mercedes')) return 'Mercedes-Benz';
      if (lower === 'bmw') return 'BMW';
      if (lower === 'vw' || lower === 'volkswagen') return 'Volkswagen';
      if (lower === 'citroen' || lower === 'citroën') return 'Citroën';
      if (lower === 'mg') return 'MG';
      if (lower === 'ds') return 'DS';
      if (lower === 'gmc') return 'GMC';
      if (lower === 'jcb') return 'JCB';
      if (lower === 'mini') return 'MINI';
      if (lower === 'smart') return 'smart';
      return (make || '').trim();
    };

    const categoryToVehicleMap: Record<string, string[]> = {
      car: ['cars', 'suv'],
      truck: ['heavy_truck', 'light_truck', 'bus'],
      motorcycle: ['motorcycle', 'scooter'],
      van: ['van', 'caravan', 'camper'],
      trailer: ['trailer'],
      agricultural: ['agricultural', 'tractor'],
      construction: ['construction'],
      boat: ['boat', 'yacht', 'jet_ski'],
      atv: ['atv', 'utv'],
    };
    const allowedCategories = vehicleType ? (categoryToVehicleMap[vehicleType] || []) : [];

    const allowedMakes = new Set<string>();
    const allowedModelsByMake: Record<string, Set<string>> = {};
    (mmData as any[]).forEach((row: any) => {
      if (!row.make) return;
      if (vehicleType && allowedCategories.length > 0) {
        const code = row.categories?.code;
        if (!code || !allowedCategories.includes(code)) return;
      }
      const make = normalizeMakeName(row.make);
      allowedMakes.add(make);
      if (row.model) {
        if (!allowedModelsByMake[make]) allowedModelsByMake[make] = new Set();
        allowedModelsByMake[make].add(row.model);
      }
    });

    const makeCounts: Record<string, number> = {};
    realMakeCounts.forEach((row) => {
      const make = normalizeMakeName(row.make);
      if (vehicleType && allowedMakes.size > 0 && !allowedMakes.has(make)) return;
      makeCounts[make] = (makeCounts[make] || 0) + Number(row.count || 0);
    });

    const makes = Object.entries(makeCounts)
      .filter(([, count]) => count > 0)
      .map(([make, count]) => ({ value: make, label: make, count }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ro', { sensitivity: 'base' }));

    const modelAccumulator: Record<string, Record<string, number>> = {};
    realModelCounts.forEach((row) => {
      const make = normalizeMakeName(row.make);
      if (vehicleType && allowedMakes.size > 0 && !allowedMakes.has(make)) return;
      const allowedSet = allowedModelsByMake[make];
      if (vehicleType && allowedSet && allowedSet.size > 0 && !allowedSet.has(row.model)) return;
      if (!modelAccumulator[make]) modelAccumulator[make] = {};
      modelAccumulator[make][row.model] = (modelAccumulator[make][row.model] || 0) + Number(row.count || 0);
    });

    const modelsByMake: Record<string, Array<{ value: string; label: string; count: number }>> = {};
    Object.entries(modelAccumulator).forEach(([make, models]) => {
      modelsByMake[make] = Object.entries(models)
        .filter(([, count]) => count > 0)
        .map(([model, count]) => ({ value: model, label: model, count }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ro', { sensitivity: 'base', numeric: true }));
    });

    if (makes.length === 0 && vehicleType && FALLBACKS[vehicleType]) {
      return FALLBACKS[vehicleType];
    }

    return { makes, modelsByMake };
  }, [mmDictQ.data, makeCountsQ.data, modelCountsQ.data, vehicleType]);

  const getModelsForMake = (make: string) => {
    if (data.modelsByMake[make]) return data.modelsByMake[make];
    const normalizations: Record<string, string[]> = {
      'Mercedes-Benz': ['Mercedes', 'Mercedes Benz'],
      Mercedes: ['Mercedes-Benz', 'Mercedes Benz'],
      Volkswagen: ['VW'],
      VW: ['Volkswagen'],
      BMW: ['Bmw'],
    };
    for (const alt of normalizations[make] || []) {
      if (data.modelsByMake[alt]) return data.modelsByMake[alt];
    }
    return [];
  };

  return {
    makes: data.makes,
    getModelsForMake,
    loading,
    error: error?.message ?? null,
  };
};
